import React, { useEffect, useMemo, useState } from 'react';
import styles from './ContactModal.module.css';
import {
  Button,
  ButtonVariant,
  FormItem,
  InputType,
  Loading,
  useModal,
  useToast
} from '@fs/uikit-react';
import { getUserFriendlyErrorMessage } from '@/utils/errorHandling.ts';
import { ApiError } from '@/services/api';
import { Contact, ContactType } from '@/shared/model/contact.ts';
import { ContactApi } from '@/pages/Operator/Person/api/ContactApi.ts';
import { ContactLinkCategoriesApi } from '@/shared/api/ContactLinkCategoriesApi.ts';
import { ContactLinkCategory } from '@/shared/model/contactLinkCategory.ts';
import { useEmailValidation, useFormValidation, useRequiredValidation } from '@/utils/validationHooks.ts';

interface ContactModalProps {
  personId: string;
  contactId?: string;
  onSuccess: () => void;
}

const ContactModal: React.FC<ContactModalProps> = ({ personId, contactId, onSuccess }) => {
  const { closeModal } = useModal();
  const toast = useToast();

  const [contactType, setContactType] = useState<ContactType>(ContactType.PHONE);

  // Common state fields for all types
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [url, setUrl] = useState('');
  const [linkCategoryId, setLinkCategoryId] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [messengerName, setMessengerName] = useState('');
  const [messengerUsername, setMessengerUsername] = useState('');

  const [categories, setCategories] = useState<ContactLinkCategory[]>([]);

  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // Load categories for LINK type dropdown
    const fetchCategories = async () => {
      try {
        const response = await ContactLinkCategoriesApi.getAll();
        setCategories(response.data || []);
      } catch (error) {
        console.error('Error loading link categories', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!contactId) return;
    const fetchContact = async () => {
      setIsFetching(true);
      try {
        // There's no get by id endpoint in docs, so we reuse list and find locally
        // Alternatively we rely on parent to pass id and we already have list; keeping simple fetch all
        const listResp = await ContactApi.getByPersonId(personId);
        const existing = (listResp.data || []).find(c => c.id === contactId);
        if (existing) {
          setContactType(existing.type);
          if (existing.type === ContactType.PHONE) setPhoneNumber(existing.phoneNumber || '');
          if (existing.type === ContactType.EMAIL) setEmail(existing.email || '');
          if (existing.type === ContactType.LINK) {
            setUrl(existing.url || '');
            const catId = existing.category && 'id' in existing.category ? existing.category.id : '';
            setLinkCategoryId(catId || '');
          }
          if (existing.type === ContactType.ADDRESS) {
            setCountry(existing.country || '');
            setCity(existing.city || '');
            setAddressLine1(existing.addressLine1 || '');
            setAddressLine2(existing.addressLine2 || '');
            setPostalCode(existing.postalCode || '');
          }
          if (existing.type === ContactType.MESSENGER) {
            setMessengerName(existing.name || '');
            setMessengerUsername(existing.username || '');
          }
        }
      } catch (error) {
        console.error('Error loading contact', error);
        toast.showErrorToast('Помилка', getUserFriendlyErrorMessage(error as ApiError));
      } finally {
        setIsFetching(false);
      }
    };
    fetchContact();
  }, [contactId, personId, toast]);

  // Validation
  const [phoneValid, phoneError, validatePhone] = useRequiredValidation(phoneNumber, 'Вкажіть номер телефону');
  const [emailValid, emailError, validateEmailField] = useEmailValidation(email, true);
  const [urlValid, urlError, validateUrl] = useRequiredValidation(url, 'Вкажіть URL');
  const [catValid, catError, validateCat] = useRequiredValidation(linkCategoryId, 'Оберіть категорію');
  const [messNameValid, messNameError, validateMessName] = useRequiredValidation(messengerName, 'Вкажіть назву месенджера');
  const [messUserValid, messUserError, validateMessUser] = useRequiredValidation(messengerUsername, 'Вкажіть username');

  const validators = useMemo(() => {
    switch (contactType) {
      case ContactType.PHONE:
        return [validatePhone];
      case ContactType.EMAIL:
        return [validateEmailField];
      case ContactType.LINK:
        return [validateUrl, validateCat];
      case ContactType.ADDRESS:
        // optional fields for address, allow empty but require at least one main field
        return [() => Boolean(country || city || addressLine1)];
      case ContactType.MESSENGER:
        return [validateMessName, validateMessUser];
      default:
        return [() => true];
    }
  }, [contactType, validatePhone, validateEmailField, validateUrl, validateCat, country, city, addressLine1, validateMessName, validateMessUser]);

  const [formValid, validateForm] = useFormValidation(validators as unknown as (() => boolean)[]);

  const contactTypeOptions = [
    { value: ContactType.PHONE, label: 'Телефон' },
    { value: ContactType.EMAIL, label: 'Email' },
    { value: ContactType.LINK, label: 'Посилання' },
    { value: ContactType.ADDRESS, label: 'Адреса' },
    { value: ContactType.MESSENGER, label: 'Месенджер' },
  ];

  const categoryOptions = useMemo(() => (categories || []).map(c => ({ value: c.id, label: c.name })), [categories]);

  const buildPayload = (): Omit<Contact, 'id'> | Contact => {
    switch (contactType) {
      case ContactType.PHONE:
        return { type: ContactType.PHONE, phoneNumber };
      case ContactType.EMAIL:
        return { type: ContactType.EMAIL, email };
      case ContactType.LINK:
        return { type: ContactType.LINK, url, category: linkCategoryId ? { id: linkCategoryId } : undefined } as any;
      case ContactType.ADDRESS:
        return { type: ContactType.ADDRESS, country, city, addressLine1, addressLine2, postalCode } as any;
      case ContactType.MESSENGER:
        return { type: ContactType.MESSENGER, name: messengerName, username: messengerUsername } as any;
      default:
        return { type: contactType } as any;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.showErrorToast('Помилка валідації', 'Будь ласка, перевірте поля форми');
      return;
    }

    setIsSaving(true);
    try {
      const data = buildPayload();
      if (contactId) {
        await ContactApi.update(contactId, { ...(data as Contact), id: contactId });
        toast.showSuccessToast('Успішно', 'Контакт оновлено');
      } else {
        await ContactApi.addForPerson(personId, data as Omit<Contact, 'id'>);
        toast.showSuccessToast('Успішно', 'Контакт створено');
      }
      closeModal();
      onSuccess();
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.showErrorToast('Помилка збереження', getUserFriendlyErrorMessage(error as ApiError));
    } finally {
      setIsSaving(false);
    }
  };

  if (isFetching) {
    return <Loading text="Завантаження контакту..." />;
  }

  return (
    <div className={styles.modalContent}>
      <div className={styles.row}>
        <FormItem
          label="Тип контакту"
          type={InputType.SELECT}
          value={contactType}
          onChange={(e) => setContactType(e.target.value as ContactType)}
          options={contactTypeOptions}
          disabled={Boolean(contactId)}
        />
      </div>

      {contactType === ContactType.PHONE && (
        <div className={styles.row}>
          <FormItem
            label="Номер телефону"
            type={InputType.TEXT}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            error={!phoneValid}
            errorMessage={phoneError}
          />
        </div>
      )}

      {contactType === ContactType.EMAIL && (
        <div className={styles.row}>
          <FormItem
            label="Email"
            type={InputType.EMAIL}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!emailValid}
            errorMessage={emailError}
          />
        </div>
      )}

      {contactType === ContactType.LINK && (
        <>
          <div className={styles.row}>
            <FormItem
              label="URL"
              type={InputType.TEXT}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              error={!urlValid}
              errorMessage={urlError}
            />
          </div>
          <div className={styles.row}>
            <FormItem
              label="Категорія"
              type={InputType.SELECT}
              value={linkCategoryId}
              onChange={(e) => setLinkCategoryId(e.target.value)}
              options={categoryOptions}
              error={!catValid}
              errorMessage={catError}
            />
          </div>
        </>
      )}

      {contactType === ContactType.ADDRESS && (
        <>
          <div className={styles.row}>
            <FormItem label="Країна" type={InputType.TEXT} value={country} onChange={(e) => setCountry(e.target.value)} />
            <FormItem label="Місто" type={InputType.TEXT} value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
          <div className={styles.row}>
            <FormItem label="Адреса (рядок 1)" type={InputType.TEXT} value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} />
            <FormItem label="Адреса (рядок 2)" type={InputType.TEXT} value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} />
          </div>
          <div className={styles.row}>
            <FormItem label="Поштовий індекс" type={InputType.TEXT} value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
          </div>
        </>
      )}

      {contactType === ContactType.MESSENGER && (
        <div className={styles.row}>
          <FormItem
            label="Месенджер"
            type={InputType.TEXT}
            value={messengerName}
            onChange={(e) => setMessengerName(e.target.value)}
            error={!messNameValid}
            errorMessage={messNameError}
          />
          <FormItem
            label="Username"
            type={InputType.TEXT}
            value={messengerUsername}
            onChange={(e) => setMessengerUsername(e.target.value)}
            error={!messUserValid}
            errorMessage={messUserError}
          />
        </div>
      )}

      <div className={styles.buttonGroup}>
        <Button
          title={isSaving ? 'Збереження...' : (contactId ? 'Оновити' : 'Створити')}
          onClick={handleSubmit}
          loading={isSaving}
        />
        <Button title="Скасувати" onClick={closeModal} variant={ButtonVariant.SECONDARY} />
      </div>
    </div>
  );
};

export default ContactModal;
